/* eslint-disable max-lines */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { Box } from '@material-ui/core';
import type {
  TicketingInstructionResponseArgs,
  TicketingMethodKey2
} from '@this/src/domain/arrangement/ticketing_instruction';
import TicketingInstruction, {
  convertTicketingInstructionResponseToArgs,
  TicketingMethod,
  TicketingMethod2,
  TicketingMethodKeys2
} from '@this/src/domain/arrangement/ticketing_instruction';
import {
  PreferredTicketingMethodLabel,
  PreferredTicketingMethodQuery,
  PreferredTicketingMethodQueryKeys
} from '@this/src/domain/arrangement/preferred_ticketing_method_query';
import type { AutoReservationStatusQueryKey } from '@this/src/domain/arrangement/auto_reservation_status_query';
import {
  AutoReservationStatusQueryDefault,
  AutoReservationStatusQueryKeys
} from '@this/src/domain/arrangement/auto_reservation_status_query';
import { isMatchingTraceId } from '@this/src/util/string/string.util';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/components/shared/ui/data_displays/table';
import { Link } from '@this/src/components/shared/ui/navigations/link';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Input } from '@this/src/components/shared/ui/inputs/input';
import { Select } from '@this/src/components/shared/ui/inputs/select';
import { Datepicker } from '@this/src/components/shared/ui/inputs/datepicker';
import { Radio } from '@this/src/components/shared/ui/inputs/radio';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher, getParam, HTTPError } from '@this/src/util';
import { TransportElementAttachFileForm } from '@this/components/arrangement/shared/price_change_form/price_change_form_item/transport_element_form/transport_element_attach_file_form';
import Notification from '@this/src/notification';
import TransportElementAttachFile from '@this/src/domain/transport_element_attach_file';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@this/shared/ui/feedbacks/modal';
import { useConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal';
import moment from 'moment';
import type { TabType } from '../todo_list';
import { SearchArea, SearchBlock, SearchLabel } from '../search_area';
import useOrganizationSelector from '../use_organization_selector';
import LogModal from '../log_modal';
import AutoReservationStatusSelector from '../auto_reservation_status_selector';

type SortKey = 'organization' | 'received_at' | 'started_at';
type SortDirection = 'asc' | 'desc';
type UploadFileData = {
  [traceId: string]: {
    [transportElementId: string]: {
      [travelerIdKey: string]: Array<
        | {
            id: number;
            transport_element_id: number;
            traveler_information_id: string | null;
            trip_id: number;
            upload_file_name: string;
            content_type: string;
            file_path: string;
            created_at: string;
            updated_at: string;
            upload_file_base64?: never; // 登録済みファイルはBase64を持たない
          }
        | {
            upload_file_name: string;
            content_type: string;
            traveler_information_id: string | null;
            upload_file_base64: string;
            // 新規ファイルには持たない項目
            id?: never;
            transport_element_id?: never;
            trip_id?: never;
            file_path?: never;
            created_at?: never;
            updated_at?: never;
          }
      >;
    };
  };
};
interface Response {
  ticketing_instructions: TicketingInstructionResponseArgs[];
  total_count: number;
  total_page: number;
}

const tab: TabType = 'TICKETING';

const getDefaultOnlyPremiumSupport = (): boolean => {
  return getParam('only_premium_support') === 'true';
};

const getDefaultPreferredTicketingMethod = (): keyof typeof PreferredTicketingMethodQuery => {
  return (getParam('preferred_ticketing_method') || 'all') as keyof typeof PreferredTicketingMethodQuery;
};

const getDefaultTicketingMethod = (): TicketingMethodKey2 => {
  return (getParam('ticketing_method') || 'all') as TicketingMethodKey2;
};

const getDefaultSortKey = (): SortKey => {
  return (getParam('sort_key') || 'organization') as SortKey;
};

const getDefaultSortDirection = (): SortDirection => {
  return (getParam('sort_direction') || 'asc') as SortDirection;
};

const getDefaultStartDateFrom = (): string | null => {
  return getParam('start_date_from') || null;
};

const getDefaultStartDateTo = (): string | null => {
  return getParam('start_date_to') || null;
};

const getDefaultReceivedDatetimeFrom = (): string | null => {
  return getParam('received_datetime_from') || null;
};

const getDefaultReceivedDatetimeTo = (): string | null => {
  return getParam('received_datetime_to') || null;
};

const getDefaultAutoReservationStatuses = (): AutoReservationStatusQueryKey[] => {
  const raw = getParam('auto_reservation_statuses');
  if (raw === undefined) {
    return [...AutoReservationStatusQueryDefault];
  }
  if (raw === null || raw === '') {
    return [];
  }
  const values = String(raw)
    .split(',')
    .map(v => v.trim())
    .filter((v): v is AutoReservationStatusQueryKey =>
      (AutoReservationStatusQueryKeys as readonly string[]).includes(v)
    );
  return values;
};

const Ticketing = observer(() => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<{ traceId: number; message: string } | null>(null);
  const [ticketingInstcutions, setTicketingInstcutions] = useState<TicketingInstruction[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [totalPage, setTotalPage] = useState(1);
  const [page, setPage] = useState(1);

  const [organizationIds, renderOrganizationSelector] = useOrganizationSelector();
  const [preferredTicketingMethod, setPreferredTicketingMethod] = useState<
    keyof typeof PreferredTicketingMethodQuery
  >(getDefaultPreferredTicketingMethod());
  const [onlyPremiumSupport, setOnlyPremiumSupport] = useState<boolean>(getDefaultOnlyPremiumSupport());
  const [ticketingMethod, setTicketingMethod] = useState<TicketingMethodKey2>(getDefaultTicketingMethod());
  const [sortKey, setSortKey] = useState<SortKey>(getDefaultSortKey());
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection());
  const [uploadFileData, setUploadFileData] = useState<UploadFileData>({});
  const [startDateFrom, setStartDateFrom] = useState<string | null>(getDefaultStartDateFrom());
  const [startDateTo, setStartDateTo] = useState<string | null>(getDefaultStartDateTo());
  const [receivedDatetimeFrom, setReceivedDatetimeFrom] = useState<string | null>(
    getDefaultReceivedDatetimeFrom()
  );
  const [receivedDatetimeTo, setReceivedDatetimeTo] = useState<string | null>(getDefaultReceivedDatetimeTo());
  const [autoReservationStatuses, setAutoReservationStatuses] = useState<AutoReservationStatusQueryKey[]>(
    getDefaultAutoReservationStatuses()
  );

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedTicketingInstruction, setSelectedTicketingInstruction] = useState<TicketingInstruction | null>(
    null
  );
  const [logModalTraceId, setLogModalTraceId] = useState<number | null>(null);
  const { open: openConfirmModal } = useConfirmModal();

  const lastValidParamsRef = useRef<Record<string, string | null>>({});

  const fetchItems = useCallback(() => {
    setIsLoading(true);
    setError(null);

    const params = {
      page,
      organization_ids: organizationIds.join(','),
      preferred_ticketing_method: preferredTicketingMethod,
      only_premium_support: onlyPremiumSupport,
      ticketing_method: ticketingMethod,
      auto_reservation_statuses: autoReservationStatuses.join(','),
      sort_key: sortKey,
      sort_direction: sortDirection,
      start_date_from: startDateFrom,
      start_date_to: startDateTo,
      received_datetime_from: receivedDatetimeFrom,
      received_datetime_to: receivedDatetimeTo
    };

    lastValidParamsRef.current = {
      start_date_from: startDateFrom,
      start_date_to: startDateTo,
      received_datetime_from: receivedDatetimeFrom,
      received_datetime_to: receivedDatetimeTo
    };

    const queryParams: Record<string, string | number | boolean | null> = { ...params };
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === null) {
        delete queryParams[key as keyof typeof queryParams];
      }
    });

    const query = _.map(queryParams, (value, key) => `${key}=${value}`).join('&');
    history.push(`/arrangement/todo_list?tab=${tab}&${query}`);

    Fetcher.get<Response>('/arrangement/ticketings.json', queryParams)
      .then(res => {
        setTicketingInstcutions(
          res.ticketing_instructions.map(
            args => new TicketingInstruction(convertTicketingInstructionResponseToArgs(args))
          )
        );
        setTotalCount(res.total_count);
        setTotalPage(res.total_page);
        if (res.total_page < page) {
          setPage(1);
        }

        // upload_file_data を設定
        const newUploadFileData = res.ticketing_instructions.reduce((acc, instruction) => {
          const traceId = instruction.trace_id;
          if (!acc[traceId]) {
            acc[traceId] = {};
          }
          instruction.transport_elements?.forEach(element => {
            if (!acc[traceId][element.id]) acc[traceId][element.id] = {};

            (element as any)?.transport_element_attach_files?.forEach((file: any) => {
              const travelerId = file.traveler_information_id?.toString() || 'null';
              if (!acc[traceId][element.id][travelerId]) {
                acc[traceId][element.id][travelerId] = [];
              }
              // ファイルデータを追加
              acc[traceId][element.id][travelerId].push({
                id: file.id,
                transport_element_id: element.id,
                traveler_information_id: file.traveler_information_id,
                trip_id: instruction.trip_id,
                upload_file_name: file.upload_file_name,
                content_type: file.content_type,
                file_path: file.file_path,
                created_at: file.created_at,
                updated_at: file.updated_at
              });
            });
          });
          return acc;
        }, {} as UploadFileData);
        setUploadFileData(newUploadFileData);
      })
      .catch(e => {
        if (e instanceof HTTPError && e.response?.data.errors) {
          const errorMsg =
            typeof e.response.data.errors === 'string'
              ? e.response.data.errors
              : JSON.stringify(e.response.data.errors);
          alert(errorMsg);

          setStartDateFrom(lastValidParamsRef.current.start_date_from);
          setStartDateTo(lastValidParamsRef.current.start_date_to);
          setReceivedDatetimeFrom(lastValidParamsRef.current.received_datetime_from);
          setReceivedDatetimeTo(lastValidParamsRef.current.received_datetime_to);
        } else {
          throw e;
        }
      })
      .finally(() => setIsLoading(false));
  }, [
    page,
    organizationIds,
    preferredTicketingMethod,
    onlyPremiumSupport,
    ticketingMethod,
    autoReservationStatuses,
    sortKey,
    sortDirection,
    startDateFrom,
    startDateTo,
    receivedDatetimeFrom,
    receivedDatetimeTo,
    history
  ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSendTicketing = (ti: TicketingInstruction) => {
    const hasEmptyReservationNumber = ti.reservationNumbers.some(
      num => num === null || num === undefined || num.trim() === ''
    );
    if (hasEmptyReservationNumber) {
      Notification.error('予約番号が空欄です');
      return;
    }

    const isQrMethod = ti.ticketingMethod != null && ['qr_east', 'qr_west'].includes(ti.ticketingMethod);
    const hasTravelerWithoutAttachFile = ti.travelerInformations?.some(traveler => {
      const travelerIdKey = traveler.id?.toString() || 'null';
      return (
        !uploadFileData[ti.traceId] ||
        Object.values(uploadFileData[ti.traceId]).every(
          filesByTraveler => !filesByTraveler[travelerIdKey] || filesByTraveler[travelerIdKey].length === 0
        )
      );
    });

    // PDFが添付されているかチェック
    const hasAttachedFiles =
      !!uploadFileData[ti.traceId] &&
      Object.values(uploadFileData[ti.traceId])
        .flat()
        .some(file => file && typeof file === 'object' && Object.keys(file).length > 0);

    if (hasAttachedFiles) {
      setSelectedTicketingInstruction(ti);
      setIsConfirmModalOpen(true);
      return;
    }

    if (isQrMethod && hasTravelerWithoutAttachFile) {
      handleQrConfirmModal(ti);
      return;
    }

    sendTicketing(ti);
  };

  const handleQrConfirmModal = async (ti: TicketingInstruction) => {
    const { result } = await openConfirmModal({
      description: 'ファイルが添付されていませんが、このまま発券完了しますか？',
      submitButtonLabel: '発券完了'
    });

    if (result) {
      sendTicketing(ti);
    }
  };

  const sendTicketing = useCallback(
    (ti: TicketingInstruction) => {
      setIsUpdating(true);
      setError(null);

      const params = {
        trace_id: ti.traceId,
        reservation_numbers: ti.reservationNumbers,
        purchase_dates: ti.purchaseDates.map(d => d?.format('YYYY-MM-DD')),
        upload_file_data: uploadFileData[ti.traceId] || {}
      };
      // アップロードされたファイルがあるかを確認
      const hasUploadedFiles =
        !!uploadFileData[ti.traceId] &&
        Object.values(uploadFileData[ti.traceId])
          .flat()
          .some(file => file && typeof file === 'object' && Object.keys(file).length > 0);
      Fetcher.put<Response>('/arrangement/ticketings/proceed.json', params)
        .then(() => {
          if (hasUploadedFiles) {
            Notification.success('アップロードが完了しました');
          }
          fetchItems();
        })
        .catch(e => {
          setError({
            traceId: ti.traceId,
            message:
              e instanceof HTTPError && e.response?.data.errors
                ? e.response.data.errors.join('\n')
                : 'エラーが発生しました'
          });
        })
        .finally(() => {
          setIsUpdating(false);
        });
    },
    [fetchItems, uploadFileData]
  );

  const revertTicketingInstruction = useCallback(
    (ti: TicketingInstruction) => {
      setIsUpdating(true);
      setError(null);
      const params = {
        order_item_id: ti.orderItemId
      };
      Fetcher.put<Response>('/arrangement/ticketing_instructions/revert.json', params)
        .then(() => fetchItems())
        .catch(e => {
          setError({
            traceId: ti.traceId,
            message:
              e instanceof HTTPError && e.response?.data.errors
                ? e.response.data.errors.join('\n')
                : 'エラーが発生しました'
          });
        })
        .finally(() => setIsUpdating(false));
    },
    [fetchItems]
  );

  const isConvertWordToBold = (message: string): boolean => {
    return message.startsWith('座席：') && message.startsWith('座席：指定席') === false;
  };

  const handleUploadFileChange = (
    file: File,
    traceId: string,
    transportElementId: number,
    travelerInformationId: string | null
  ) => {
    if (!file) return;
    TransportElementAttachFile.convertFileToBase64(file)
      .then(base64String => {
        const newFileData = {
          upload_file_name: file.name,
          content_type: file.type,
          traveler_information_id: travelerInformationId,
          upload_file_base64: base64String
        };

        setUploadFileData(prev => {
          const travelerIdKey = travelerInformationId ?? 'null';
          const updated = { ...prev };
          if (!updated[traceId]) {
            updated[traceId] = {};
          }
          if (!updated[traceId][transportElementId]) {
            updated[traceId][transportElementId] = {};
          }
          if (!updated[traceId][transportElementId][travelerIdKey]) {
            updated[traceId][transportElementId][travelerIdKey] = [];
          }
          if (!updated[traceId][transportElementId]) {
            updated[traceId][transportElementId] = {};
          }
          updated[traceId][transportElementId][travelerIdKey].push(newFileData);
          return updated;
        });
      })
      .catch(error => {
        console.error('Failed to convert file to Base64', error);
      });
  };

  const handleDeleteFile = (
    traceId: string,
    transportElementId: number,
    travelerInformationId: string | null,
    index: number
  ) => {
    setUploadFileData(prev => {
      const travelerIdKey = travelerInformationId ?? 'null';
      const updated = { ...prev };

      // traceId,transportElementId,travelerIdKeyが存在する場合のみ処理
      if (updated[traceId]?.[transportElementId]?.[travelerIdKey]?.length > index) {
        updated[traceId][transportElementId][travelerIdKey].splice(index, 1);

        // ファイルがなくなった場合、該当のKey を削除
        if (updated[traceId][transportElementId][travelerIdKey].length === 0) {
          delete updated[traceId][transportElementId][travelerIdKey];
        }
        if (Object.keys(updated[traceId][transportElementId]).length === 0) {
          delete updated[traceId][transportElementId];
        }
        if (Object.keys(updated[traceId]).length === 0) {
          delete updated[traceId];
        }
      }
      return updated;
    });
  };

  const handleStartDateFromChange = (date: moment.Moment | null) => {
    setStartDateFrom(date ? date.format('YYYY-MM-DD') : null);
  };

  const handleStartDateToChange = (date: moment.Moment | null) => {
    setStartDateTo(date ? date.format('YYYY-MM-DD') : null);
  };

  const handleReceivedDatetimeFromChange = (date: moment.Moment | null) => {
    setReceivedDatetimeFrom(date ? date.second(0).format('YYYY-MM-DD HH:mm:ss') : null);
  };

  const handleReceivedDatetimeToChange = (date: moment.Moment | null) => {
    setReceivedDatetimeTo(date ? date.second(59).format('YYYY-MM-DD HH:mm:ss') : null);
  };

  const getAttachedFiles = (ti: TicketingInstruction) => {
    const files: Array<{
      fileName: string;
      contentType: string;
      base64?: string;
      filePath?: string;
      travelerName?: string;
    }> = [];

    if (uploadFileData[ti.traceId]) {
      Object.entries(uploadFileData[ti.traceId]).forEach(([transportElementId, filesByTraveler]) => {
        Object.entries(filesByTraveler).forEach(([travelerIdKey, fileList]) => {
          fileList.forEach(file => {
            let travelerName = '未指定';

            if (travelerIdKey === 'null') {
              // traveler_information_idがnullの場合、最初の出張者を表示
              if (ti.travelerInformations && ti.travelerInformations.length > 0) {
                travelerName = ti.travelerInformations[0].name || '未指定';
              }
            } else {
              // 特定の出張者IDに紐づくファイルの場合
              const traveler = ti.travelerInformations?.find(t => t.id?.toString() === travelerIdKey);
              if (traveler) {
                travelerName = traveler.name || '未指定';
              }
            }

            files.push({
              fileName: file.upload_file_name,
              contentType: file.content_type,
              base64: 'upload_file_base64' in file ? file.upload_file_base64 : undefined,
              filePath: 'file_path' in file ? file.file_path : undefined,
              travelerName
            });
          });
        });
      });
    }

    return files;
  };

  const renderPdfPreview = (file: {
    fileName: string;
    contentType: string;
    base64?: string;
    filePath?: string;
  }) => {
    if (!file.contentType.includes('pdf')) {
      return <div>PDFファイルではありません</div>;
    }

    if (file.base64) {
      return (
        <iframe
          src={`data:${file.contentType};base64,${file.base64}`}
          style={{
            width: '600px',
            height: '800px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
          title={file.fileName}
        />
      );
    }

    if (file.filePath) {
      return (
        <iframe
          src={`/arrangement/ticketings/download_file?file_path=${encodeURIComponent(file.filePath)}`}
          style={{
            width: '600px',
            height: '800px',
            border: '1px solid #ccc',
            borderRadius: '4px'
          }}
          title={file.fileName}
        />
      );
    }

    return <div>ファイルの表示に失敗しました</div>;
  };

  return (
    <>
      <SearchArea>
        <SearchBlock>
          <SearchLabel>法人</SearchLabel>
          {renderOrganizationSelector()}
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>旅程の発券方法</SearchLabel>
          <Select
            value={preferredTicketingMethod}
            onChange={e =>
              setPreferredTicketingMethod(e.target.value as keyof typeof PreferredTicketingMethodQuery)
            }
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {PreferredTicketingMethodQueryKeys.map(key => (
              <option key={key} value={key}>
                {PreferredTicketingMethodQuery[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>発券方法</SearchLabel>
          <Select
            value={ticketingMethod}
            onChange={e => setTicketingMethod(e.target.value as TicketingMethodKey2)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            {TicketingMethodKeys2.map(key => (
              <option key={key} value={key}>
                {TicketingMethod2[key]}
              </option>
            ))}
          </Select>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>自動予約ステータス</SearchLabel>
          <AutoReservationStatusSelector
            selectedStatuses={autoReservationStatuses}
            onChange={setAutoReservationStatuses}
          />
        </SearchBlock>
        <SearchBlock>
          <Checkbox checked={onlyPremiumSupport} onChange={e => setOnlyPremiumSupport(e.target.checked)}>
            <b style={{ fontSize: '12px' }}>土日祝オプション企業のみ</b>
          </Checkbox>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>出発日</SearchLabel>
          <Box display="flex" alignItems="center">
            <Datepicker
              value={startDateFrom ? new Date(startDateFrom) : undefined}
              onChange={date => handleStartDateFromChange(date ? moment(date) : null)}
              placeholder="開始日"
              fullWidth
            />
            <Box mx={1}>〜</Box>
            <Datepicker
              value={startDateTo ? new Date(startDateTo) : undefined}
              onChange={date => handleStartDateToChange(date ? moment(date) : null)}
              placeholder="終了日"
              fullWidth
            />
          </Box>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>依頼日</SearchLabel>
          <Box display="flex" alignItems="center">
            <Datepicker
              type="datetime-local"
              value={receivedDatetimeFrom ? new Date(receivedDatetimeFrom) : undefined}
              onChange={date => handleReceivedDatetimeFromChange(date ? moment(date) : null)}
              placeholder="開始日時"
              fullWidth
            />
            <Box mx={1}>〜</Box>
            <Datepicker
              type="datetime-local"
              value={receivedDatetimeTo ? new Date(receivedDatetimeTo) : undefined}
              onChange={date => handleReceivedDatetimeToChange(date ? moment(date) : null)}
              placeholder="終了日時"
              fullWidth
            />
          </Box>
        </SearchBlock>
        <SearchBlock>
          <SearchLabel>並び順</SearchLabel>
          <select
            value={sortKey}
            onChange={e => setSortKey(e.target.value as SortKey)}
            style={{ marginBottom: 0, marginRight: '5px' }}
          >
            <option value="organization">法人</option>
            <option value="received_at">依頼日</option>
            <option value="started_at">出発日</option>
          </select>
          <Radio
            checked={sortDirection === 'asc'}
            onChange={() => setSortDirection('asc')}
            style={{ marginRight: '5px' }}
          >
            昇順▲
          </Radio>
          <Radio checked={sortDirection === 'desc'} onChange={() => setSortDirection('desc')}>
            降順▼
          </Radio>
        </SearchBlock>
      </SearchArea>
      <Box>未処理件数: {totalCount}件</Box>
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>出発日</TableCell>
                <TableCell>依頼日</TableCell>
                <TableCell>企業名</TableCell>
                <TableCell>旅程の発券方法</TableCell>
                <TableCell>土日祝対応</TableCell>
                <TableCell>発券方法</TableCell>
                <TableCell>備考</TableCell>
                <TableCell>チャットメッセージ</TableCell>
                <TableCell>TripID</TableCell>
                <TableCell>TraceID</TableCell>
                <TableCell>最新のログメッセージ</TableCell>
                <TableCell>内容</TableCell>
                <TableCell>予約番号</TableCell>
                <TableCell>仕入日</TableCell>
                <TableCell>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ticketingInstcutions.map(ti => (
                <TableRow key={ti.orderItemId}>
                  <TableCell nowrap>{ti.startDate?.format('YYYY-MM-DD') || ''}</TableCell>
                  <TableCell nowrap>{ti.receivedAt.format('YYYY-MM-DD HH:mm')}</TableCell>
                  <TableCell>{ti.organizationName}</TableCell>
                  <TableCell>
                    {ti.preferredTicketingMethod
                      ? PreferredTicketingMethodLabel[ti.preferredTicketingMethod]
                      : PreferredTicketingMethodLabel.unset}
                  </TableCell>
                  <TableCell>{ti.premiumSupport ? '◯' : ''}</TableCell>
                  <TableCell>{ti.ticketingMethod ? TicketingMethod[ti.ticketingMethod] : ''}</TableCell>
                  <TableCell>{ti.ticketingNote}</TableCell>
                  <TableCell>
                    {ti.latestMessage?.split('\n').map((line, index) => (
                      <Box key={index}>{line}</Box>
                    ))}
                  </TableCell>
                  <TableCell nowrap>
                    <Link
                      href={`/arrangement/virtual_counter?trip_id=${ti.tripId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ti.tripId}
                    </Link>
                  </TableCell>
                  <TableCell nowrap>{ti.traceId}</TableCell>
                  <TableCell>
                    {ti.latestLog && <span>{ti.latestLog}</span>}
                    <p>
                      <Link onClick={() => setLogModalTraceId(ti.traceId)}>ログ</Link>)
                    </p>
                  </TableCell>{' '}
                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                    {ti.content?.split('\n').map((line, index) => (
                      <React.Fragment key={index}>
                        {isConvertWordToBold(line) ? <strong>{line}</strong> : <span>{line}</span>}
                        {index !== (ti.content?.split('\n').length || 0) - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </TableCell>
                  <TableCell style={{ minWidth: '250px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    <>
                      {ti.reservationNumbers.map((reservationNumber, i) => {
                        const isTraceIdMatching = isMatchingTraceId(reservationNumber || '', ti.traceId);
                        return (
                          <Box key={i}>
                            <Input
                              value={reservationNumber || ''}
                              onChange={e => {
                                ti.reservationNumbers[i] = e.target.value;
                              }}
                              style={{
                                backgroundColor: isTraceIdMatching ? '#ffebee' : undefined,
                                borderColor: isTraceIdMatching ? '#f8bbd9' : undefined
                              }}
                            />
                          </Box>
                        );
                      })}
                      {ti.ticketingMethod != null &&
                        ['qr_west', 'qr_east'].includes(ti.ticketingMethod) &&
                        ti?.transportElements?.map((element, j) => (
                          <Box key={j} padding="5px">
                            <TransportElementAttachFileForm
                              attachFiles={element.transportElementAttachFiles}
                              travelerInformations={ti?.travelerInformations || []}
                              attachableType={element.type}
                              attachableId={element.id}
                              onChange={(file, transportElementId, travelerInformationId) =>
                                handleUploadFileChange(
                                  file,
                                  String(ti.traceId),
                                  element.id || 0,
                                  travelerInformationId
                                )
                              }
                              onDelete={(transportElementId, travelerInformationId, index) =>
                                handleDeleteFile(String(ti.traceId), element.id || 0, travelerInformationId, index)
                              }
                            />
                          </Box>
                        ))}
                    </>
                  </TableCell>
                  <TableCell style={{ minWidth: '160px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {ti.purchaseDates.map((date, i) => (
                      <Datepicker
                        key={date ? date.valueOf() : `purchaseDate-${i}`}
                        value={date?.toDate()}
                        onChange={d => {
                          ti.purchaseDates[i] = d ? moment(d) : null;
                        }}
                        fullWidth
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Button loading={isUpdating} onClick={() => handleSendTicketing(ti)}>
                        発券完了
                      </Button>
                    </Box>
                    <Box marginTop="10px">
                      <Button loading={isUpdating} color="sub" onClick={() => revertTicketingInstruction(ti)}>
                        発券指示に戻す
                      </Button>
                    </Box>
                    {error?.traceId === ti.traceId && (
                      <Box marginTop="10px" color="red">
                        {error.message}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* 発券確認モーダル */}
          <Modal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} size="large">
            <ModalHeader>発券内容確認</ModalHeader>
            <ModalBody>
              {selectedTicketingInstruction && (
                <Box>
                  {/* 内容 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      内容
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.content?.split('\n').map((line, index) => (
                        <React.Fragment key={index}>
                          {isConvertWordToBold(line) ? <strong>{line}</strong> : <>{line}</>}
                          {index !== (selectedTicketingInstruction.content?.split('\n').length || 0) - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>

                  {/* 予約番号 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      予約番号
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.reservationNumbers.map((reservationNumber, i) => (
                        <Box key={i} marginBottom="5px">
                          {reservationNumber || '未入力'}
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {/* 出張者名 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      出張者名
                    </Box>
                    <Box>
                      {selectedTicketingInstruction.travelerInformations &&
                      selectedTicketingInstruction.travelerInformations.length > 0 ? (
                        selectedTicketingInstruction.travelerInformations.map((traveler, i) => (
                          <Box key={i} marginBottom="5px">
                            {traveler.name || '名前未設定'}
                          </Box>
                        ))
                      ) : (
                        <Box marginBottom="5px" color="gray">
                          出張者情報がありません
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* 添付ファイル一覧 */}
                  <Box marginBottom="20px">
                    <Box fontWeight="bold" marginBottom="10px">
                      添付ファイル
                    </Box>
                    {getAttachedFiles(selectedTicketingInstruction).map((file, index) => (
                      <Box
                        key={index}
                        marginBottom="15px"
                        padding="10px"
                        border="1px solid #eee"
                        borderRadius="4px"
                      >
                        <Box fontWeight="bold" marginBottom="5px">
                          {file.fileName} ({file.travelerName})
                        </Box>
                        {file.contentType.includes('pdf') && <Box marginTop="10px">{renderPdfPreview(file)}</Box>}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="sub" onClick={() => setIsConfirmModalOpen(false)}>
                キャンセル
              </Button>
              <Button
                onClick={() => {
                  if (selectedTicketingInstruction) {
                    sendTicketing(selectedTicketingInstruction);
                  }
                  setIsConfirmModalOpen(false);
                  setSelectedTicketingInstruction(null);
                }}
              >
                確認完了
              </Button>
            </ModalFooter>
          </Modal>

          {/* ログモーダル */}
          {logModalTraceId !== null && (
            <LogModal traceId={logModalTraceId} onClose={() => setLogModalTraceId(null)} />
          )}

          <TablePagination
            count={totalPage}
            page={page}
            onChange={(_: unknown, newPage: number) => setPage(newPage)}
          />
        </>
      )}
    </>
  );
});

export default Ticketing;
